import type { IntlShape } from 'react-intl'
import type { ImpactReport } from '../types'

/**
 * The fictional cohort behind the marketing page.
 *
 * Invented from scratch: a six month parcours d'insertion with 38 participants,
 * 31 of whom answered at least twice. No real organization is named and no real
 * organization's published numbers are reproduced here. One measure stays flat
 * on purpose, because a report where everything improves reads as invented.
 *
 * Scores are per question averages on the 0 to 5 rating scale the app uses, so
 * the cohort total is out of 25.
 */

export const COHORT_PARTICIPANTS = 38
export const COHORT_MULTI_ATTEMPT = 31
export const COHORT_EXCLUDED = 7

interface MeasureSpec {
  id: string
  textId: string
  baseline: number
  latest: number
}

const MEASURES: MeasureSpec[] = [
  { id: 'confiance', textId: 'marketing.cohort.measure.confidence', baseline: 2.3, latest: 3.8 },
  { id: 'assiduite', textId: 'marketing.cohort.measure.attendance', baseline: 2.9, latest: 4.1 },
  { id: 'technique', textId: 'marketing.cohort.measure.skills', baseline: 1.9, latest: 3.9 },
  { id: 'recherche', textId: 'marketing.cohort.measure.jobSearch', baseline: 2.2, latest: 3.4 },
  { id: 'logement', textId: 'marketing.cohort.measure.housing', baseline: 3.1, latest: 3.2 },
]

/** Same threshold the backend uses to classify a delta (impact-comparison spec). */
function direction(delta: number): string {
  if (delta > 0.01) return 'improved'
  if (delta < -0.01) return 'declined'
  return 'unchanged'
}

const round1 = (n: number) => Math.round(n * 10) / 10

export function buildCohortReport(intl: IntlShape): ImpactReport {
  const per_question = MEASURES.map((m) => {
    const delta = round1(m.latest - m.baseline)
    return {
      question_id: m.id,
      question_text: intl.formatMessage({ id: m.textId }),
      baseline_avg: m.baseline,
      latest_avg: m.latest,
      delta,
      delta_percent: Math.round((delta / m.baseline) * 1000) / 10,
      // The housing measure moves by 0.1, which the backend calls unchanged at
      // its 0.01 threshold only if the move is smaller; keep the real rule here
      // and let the number speak.
      direction: m.id === 'logement' ? 'unchanged' : direction(delta),
    }
  })

  const baseline = round1(MEASURES.reduce((sum, m) => sum + m.baseline, 0))
  const latest = round1(MEASURES.reduce((sum, m) => sum + m.latest, 0))
  const delta = round1(latest - baseline)

  return {
    survey_id: 'demo-cohort',
    survey_title: intl.formatMessage({ id: 'marketing.cohort.surveyTitle' }),
    total_respondents: COHORT_PARTICIPANTS,
    respondents_with_multiple_attempts: COHORT_MULTI_ATTEMPT,
    respondents_excluded: COHORT_EXCLUDED,
    baseline_avg_score: baseline,
    latest_avg_score: latest,
    overall_delta: delta,
    overall_delta_percent: Math.round((delta / baseline) * 1000) / 10,
    per_question,
    summary: intl.formatMessage({ id: 'marketing.cohort.summary' }, {
      people: COHORT_MULTI_ATTEMPT,
      delta: intl.formatNumber(delta, { minimumFractionDigits: 1 }),
      percent: Math.round((delta / baseline) * 100),
      excluded: COHORT_EXCLUDED,
    }),
    computed_at: '2026-09-15T13:00:00Z',
  }
}
